'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '../lib/cn';

interface CollapsibleContentProps {
  children: React.ReactNode;
  contentKey?: string | null;
  threshold?: number;
  mobileThreshold?: number;
  disabled?: boolean;
  expandLabel?: string;
  collapseLabel?: string;
  innerSelector?: string;
  /**
   * 높이 측정 전략.
   * - "auto"(기본): tiptap 등 비동기로 늘어나는 본문용. rAF + 200ms 폴링(2초) + 3s/6s 지연 측정 + 옵저버.
   * - "observer": 자체 onLoad로 높이를 확정하는 콘텐츠(예: iframe 프리뷰)용. 폴링/지연 타이머 없이
   *   초기 측정 + ResizeObserver만 사용해 로드 후 출렁임(리플로우 폭주)을 없앤다.
   */
  measureMode?: 'auto' | 'observer';
  onCollapsibleChange?: (collapsed: boolean) => void;
  buttonAlign?: 'center' | 'start' | 'end';
  fadeSize?: 'sm' | 'md';
  controlDensity?: 'default' | 'compact';
}

export function CollapsibleContent({
  children,
  contentKey,
  threshold = 320,
  mobileThreshold,
  disabled = false,
  expandLabel,
  collapseLabel,
  innerSelector = '.tiptap-viewer',
  measureMode = 'auto',
  onCollapsibleChange,
  buttonAlign = 'center',
  fadeSize = 'md',
  controlDensity = 'default',
}: CollapsibleContentProps) {
  const tCommon = useTranslations('common');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsible, setIsCollapsible] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const resolvedExpandLabel = expandLabel ?? tCommon('actions.expand');
  const resolvedCollapseLabel = collapseLabel ?? tCommon('actions.collapse');
  const effectiveThreshold = isMobileViewport && mobileThreshold !== undefined ? mobileThreshold : threshold;

  useEffect(() => {
    if (mobileThreshold === undefined) {
      setIsMobileViewport(false);
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const handleChange = () => {
      setIsMobileViewport(mediaQuery.matches);
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [mobileThreshold]);

  const measure = useCallback(() => {
    if (disabled) {
      setIsCollapsible(false);
      return;
    }

    const el = contentRef.current;
    if (!el) {
      setIsCollapsible(false);
      return;
    }

    setIsCollapsible(el.scrollHeight > effectiveThreshold);
  }, [disabled, effectiveThreshold]);

  useEffect(() => {
    onCollapsibleChange?.(isCollapsible && !isExpanded);
  }, [isCollapsible, isExpanded, onCollapsibleChange]);

  useEffect(() => {
    if (disabled) {
      setIsCollapsible(false);
      setIsExpanded(true);
      return;
    }

    setIsExpanded(false);

    const frame = window.requestAnimationFrame(() => {
      measure();
    });

    // "observer" 모드는 콘텐츠가 자체 onLoad로 높이를 확정하므로(예: iframe) 폴링/지연 타이머를 쓰지 않는다.
    // 로드 후 ResizeObserver가 높이 변화를 잡아 한 번만 재측정 → 출렁임 제거.
    const usePolling = measureMode === 'auto';

    let fallbackInterval: number | undefined;
    if (usePolling) {
      const fallbackStart = Date.now();
      fallbackInterval = window.setInterval(() => {
        measure();
        if (Date.now() - fallbackStart > 2000) {
          window.clearInterval(fallbackInterval);
        }
      }, 200);
    }

    const element = contentRef.current;
    const innerElement = element?.querySelector(innerSelector) ?? element ?? null;

    let mutationObserver: MutationObserver | null = null;
    if (innerElement && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        measure();
      });
      mutationObserver.observe(innerElement, { childList: true, subtree: true, characterData: true });
    }

    let resizeObserver: ResizeObserver | null = null;
    if (innerElement && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        measure();
      });
      resizeObserver.observe(innerElement);
    }

    let lateTimeout1: number | undefined;
    let lateTimeout2: number | undefined;
    if (usePolling) {
      lateTimeout1 = window.setTimeout(() => measure(), 3000);
      lateTimeout2 = window.setTimeout(() => measure(), 6000);
    }

    const handleResize = () => {
      measure();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.cancelAnimationFrame(frame);
      if (fallbackInterval !== undefined) window.clearInterval(fallbackInterval);
      if (lateTimeout1 !== undefined) window.clearTimeout(lateTimeout1);
      if (lateTimeout2 !== undefined) window.clearTimeout(lateTimeout2);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [contentKey, disabled, measure, innerSelector, measureMode]);

  return (
    <>
      <div
        ref={contentRef}
        className="relative w-full min-w-0"
        style={!isExpanded && isCollapsible ? { maxHeight: effectiveThreshold, overflow: 'hidden' } : undefined}
      >
        {children}
        {!isExpanded && isCollapsible ? (
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-base-100 to-transparent',
              fadeSize === 'sm' ? 'h-12' : 'h-24',
            )}
          />
        ) : null}
      </div>
      {isCollapsible ? (
        <div
          className={cn(
            'flex',
            controlDensity === 'compact' ? 'mt-1' : 'mt-3',
            buttonAlign === 'start' ? 'justify-start' : buttonAlign === 'end' ? 'justify-end' : 'justify-center',
          )}
        >
          <button
            type="button"
            className={cn('btn btn-ghost gap-1 text-xs', controlDensity === 'compact' ? 'btn-xs' : 'btn-sm')}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? resolvedCollapseLabel : resolvedExpandLabel}
            <svg
              className={cn(
                'transition-transform',
                controlDensity === 'compact' ? 'h-3.5 w-3.5' : 'h-4 w-4',
                isExpanded && 'rotate-180',
              )}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      ) : null}
    </>
  );
}
